import { capitalize, debounce } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ImageIcon from "../../Assets/images/VoxApp-Icon.png";
import { AssistantIcon } from "../../Assets/svgs/AssistantIcon";
import { CrossIcon } from "../../Assets/svgs/CrossIcon";
import OrangeRightIcon from "../../Assets/svgs/OrangeRightIcon";
import { Loader } from "../../Common/Loader";
import { Pagination } from "../../Common/Pagination";
import { getAssistantList } from "../../Redux/Slice/Assistant/AssistantsListSlice";
import { clearChatDetails } from "../../Redux/Slice/Chats/ChatDetailsSlice";
import { getChatList } from "../../Redux/Slice/Chats/ChatListSlice";
import { getGroupChatList } from "../../Redux/Slice/Chats/GroupChatListSlice";
import {
  setPage,
  setTotalItems,
  setTotalPages,
} from "../../Redux/Slice/Common/PaginationSlice";
import useDebounce from "./../../Common/useDebounce";
import useDidMountEffect from "../../Common/useDidMountEffect";
import { getWidgetChatList } from "../../Redux/Slice/Chats/WidgetChatSlice";
import { LIMIT_PER_PAGE } from "../../Common/Constants";

export const ChatSideList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isForMetaAssistant = location.pathname.includes("meta-assistant-chat");
  const [selectedTab, setSelectedTab] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [assistants, setAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [localItemsPerPage, setLocalItemsPerPage] = useState(LIMIT_PER_PAGE);
  const searchQuery = useDebounce(searchValue, 500);
  const [selectedAssistent, setSelectedAssistent] = useState(null);
  const AssistantList = useSelector((state) => state.assistants.assistantsList);
  const chatListData = useSelector((state) => state?.chatList?.chatListData);
  const selectedTabScreen = location?.pathname?.includes("/individual")
    ? "INDIVIDUAL"
    : location?.pathname?.includes("/group-chat")
    ? "GROUP_CHAT"
    : location?.pathname?.includes("/widget-chat")
    ? "WIDGET_CHAT"
    : "";
  const allPaths = location.pathname.split("/");
  const contactId = allPaths?.length > 2 ? allPaths[allPaths.length - 2] : "";
  const assistantId = allPaths?.length > 1 ? allPaths[allPaths.length - 1] : "";
  const groupChatListData = useSelector(
    (state) => state?.groupChatList?.groupChatListData
  );
  const widgetChatListData = useSelector(
    (state) => state?.widgetChatList?.widgetChatListData
  );

  const onSelectTab = (tab) => {
    setSelectedTab(tab);
  };

  const FetchAssistantsList = async () => {
    await dispatch(getAssistantList({ text: "", isDropdown: true }));
  };

  const getChatListData = async (
    page = 1,
    searchText = searchValue,
    assistant = selectedAssistent,
    isFromMount = false,
    contactId = ""
  ) => {
    setIsLoading(true);
    try {
      const chatAction =
        selectedTab == 1
          ? getChatList
          : selectedTab == 2
          ? getGroupChatList
          : getWidgetChatList;
      const result = await dispatch(
        chatAction({
          page: page,
          keyword: searchText,
          assistant: assistant,
          contactId,
        })
      );
      if (
        chatAction.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        dispatch(setPage(result?.payload?.pagination?.currentPage));
        dispatch(setTotalItems(result?.payload?.pagination?.totalItems));
        dispatch(setTotalPages(result?.payload?.pagination?.totalPages));
        if (
          Array.isArray(result?.payload?.data) &&
          isFromMount &&
          location?.state &&
          location?.state?.contactId &&
          location?.state?.assistantId
        ) {
          const chatOfContact = result?.payload?.data?.find(
            (chat) =>
              chat?.contactId == location?.state?.contactId &&
              chat?.assistantId == location?.state?.assistantId
          );
          if (chatOfContact) {
            onSelectChat(chatOfContact);
          }
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const isSelectedChat = (chat) => {
    if (
      chat?.isCallExternal &&
      location.pathname.includes("individual-external")
    ) {
      let id = chat?.emailId ? chat?.emailId : chat?.callId;
      let isEmailChat = chat?.emailId ? 1 : 0;
      return id == contactId && isEmailChat == assistantId;
    } else if (selectedTab == 3 && location.pathname.includes("widget-chat")) {
      return (
        chat?.widgetChatId == contactId && chat?.assistantId == assistantId
      );
    } else if (selectedTab == 2 && location.pathname.includes("/group")) {
      return (
        chat?.contactGroupId == contactId && chat?.assistantId == assistantId
      );
    } else {
      return chat?.contactId == contactId && chat?.assistantId == assistantId;
    }
  };
  const onSelectChat = async (chat) => {
    if (chat?.isCallExternal) {
      let id = chat?.emailId ? chat?.emailId : chat?.callId;
      let isEmailChat = chat?.emailId ? 1 : 0;
      navigate(`/chat/individual-external/${id}/${isEmailChat}`);
    } else if (selectedTab == 3) {
      navigate(`/chat/widget-chat/${chat?.widgetChatId}/${chat?.assistantId}`);
    } else {
      if (isForMetaAssistant) {
        navigate(
          `/meta-assistant-chat/individual/${chat?.contactId}/${chat?.assistantId}`
        );
      } else {
        navigate(`/chat/individual/${chat?.contactId}/${chat?.assistantId}`);
      }
    }
  };

  const debouncedGetChatList = useCallback(debounce(getChatListData, 500), [
    searchQuery,
  ]);

  const handleSearchChange = (e) => {
    setSearchValue(e?.target?.value);
    debouncedGetChatList(1, e?.target?.value);
  };

  const onSelectAssistent = (e) => {
    setSelectedAssistent(e?.target?.value);
    getChatListData(1, searchValue, e?.target?.value);
    setShowFilter(false);
  };

  useEffect(() => {
    dispatch(clearChatDetails());
    FetchAssistantsList();
  }, []);

  useEffect(() => {
    if (AssistantList) {
      setAssistants(AssistantList.data);
    }
  }, [AssistantList]);

  useEffect(() => {
    getChatListData(
      1,
      searchValue,
      selectedAssistent,
      true,
      location.state?.contactId
    );
  }, [location.state?.contactId]);

  useDidMountEffect(() => {
    getChatListData(1, searchValue, selectedAssistent, true);
  }, [selectedTab]);

  return (
    <div className="listCol">
      {isLoading ? <Loader /> : ""}
      <div className="listCol__head">
        <h2 className="listCol__head--title">
          {isForMetaAssistant ? "Customers" : "Contacts"}
        </h2>
        <div className="listCol__head--actions">
          {showFilter ? (
            <button
              className="p_0"
              type="button"
              onClick={() => setShowFilter(false)}
            >
              <CrossIcon />
            </button>
          ) : selectedAssistent ? (
            <button
              className="btn btn__primary"
              type="button"
              onClick={() => setShowFilter(true)}
            >
              <svg
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.59588 5.85534H12.4511M1.38208 1.42773H14.6649M5.80969 10.2829H10.2373"
                  stroke="white"
                  stroke-width="1.47882"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Filter Applied (1)
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAssistent(null);
                  getChatListData(1, searchValue, null);
                  setShowFilter(false);
                }}
              >
                <svg
                  width="13"
                  height="14"
                  viewBox="0 0 13 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.0471191"
                    y="0.666016"
                    width="12.3772"
                    height="12.3772"
                    rx="6.18859"
                    fill="#F6F6F6"
                  />
                  <path
                    d="M6.23568 6.28104L8.24133 4.27539L8.81426 4.84832L6.80861 6.85397L8.81426 8.85962L8.24133 9.43255L6.23568 7.4269L4.23003 9.43255L3.6571 8.85962L5.66276 6.85397L3.6571 4.84832L4.23003 4.27539L6.23568 6.28104Z"
                    fill="#4F4F4F"
                  />
                </svg>
              </div>
            </button>
          ) : (
            <button
              className="btn btn__white p_0"
              type="button"
              onClick={() => setShowFilter(true)}
            >
              <svg
                width="19"
                height="13"
                viewBox="0 0 19 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.24969 6.50036H14.7504M1.62451 1.25H17.3756M6.87487 11.7507H12.1252"
                  stroke="#475569"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      {!isForMetaAssistant ? (
        <div className="tabsWrap">
          <ul className="tabs">
            <li
              className={`tabs__link ${
                selectedTab == 1 ? "tabs__link--selected" : ""
              }`}
              onClick={() => {
                navigate("/chat");
                onSelectTab(1);
              }}
            >
              Individual
            </li>
            <li
              className={`tabs__link ${
                selectedTab == 2 ? "tabs__link--selected" : ""
              }`}
              onClick={() => {
                navigate("/chat");
                onSelectTab(2);
              }}
            >
              Groups
            </li>
            <li
              className={`tabs__link ${
                selectedTab == 3 ? "tabs__link--selected" : ""
              }`}
              onClick={() => {
                navigate("/chat");
                onSelectTab(3);
              }}
            >
              Widget
            </li>
          </ul>
        </div>
      ) : null}
      <div className="fillter">
        {showFilter ? (
          <>
            <div className="form__Field">
              <label className="form__label">Assistant</label>
              <select
                className="form__select form__select--sm"
                id="credits"
                onChange={onSelectAssistent}
                value={selectedAssistent}
              >
                <option value="1" selected disabled>
                  -Select-
                </option>
                {assistants.map((assistant) => {
                  return <option value={assistant.id}>{assistant.name}</option>;
                })}
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="form__Field">
              <div className="form__groupField">
                <span className="btn">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                      stroke="#475569"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <input
                  type="text"
                  className="form__input form__input--sm"
                  placeholder={`Search a ${
                    isForMetaAssistant
                      ? "Customer"
                      : selectedTab == 1
                      ? "Contact"
                      : selectedTab == 3
                      ? "Domain"
                      : "Group"
                  }, Assistant`}
                  value={searchValue}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div className="tabsContent">
        <div className="tabsContent__content tabsContent__content--selected">
          {!showFilter ? (
            selectedTab == 1 ? (
              Array.isArray(chatListData?.data) &&
              chatListData?.data?.length ? (
                <ul
                  className="listCol__list listing__list"
                  style={
                    isForMetaAssistant
                      ? {
                          height: "calc(100dvh - 207px)",
                        }
                      : {}
                  }
                >
                  {chatListData?.data?.map((chat) => {
                    const isSelected = isSelectedChat(chat);
                    return (
                      <li
                        className={`listCol__list__item ${
                          isSelected ? "listCol__list__item--selected" : ""
                        }`}
                        onClick={() => onSelectChat(chat)}
                      >
                        <div className="chatCard">
                          <div className="chatCard__initials">
                            <img src={chat?.contactLogo ?? ImageIcon} alt="" />
                          </div>
                          <div className="chatCard__info">
                            <div className="chatCard__info__col">
                              <h4 className="name">
                                {capitalize(chat?.contactName ?? "")}
                              </h4>
                              <p>{chat?.lastMessage}</p>
                            </div>
                            <div className="chatCard__info__col chatCard__info__col--info">
                              <p>{chat?.lastMessageTime}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flexbox">
                          <p className="para">
                            <AssistantIcon />
                            {chat?.assistantName}
                          </p>
                          {chat?.isContactDeleted ? (
                            <button className="btn btn__danger">
                              Deleted Contact
                            </button>
                          ) : chat?.isCallExternal ? (
                            <button className="btn btn__primary btn__outlined">
                              External
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <ul className="listCol__list listing__list">
                  <div className="no-data">No Chats Found!</div>
                </ul>
              )
            ) : selectedTab == 3 ? (
              Array.isArray(widgetChatListData?.data) &&
              widgetChatListData?.data?.length ? (
                <ul className="listCol__list listing__list">
                  {widgetChatListData?.data?.map((chat) => {
                    const isSelected = isSelectedChat(chat);
                    return (
                      <li
                        className={`listCol__list__item ${
                          isSelected ? "listCol__list__item--selected" : ""
                        }`}
                        onClick={() => onSelectChat(chat)}
                      >
                        <div className="chatCard">
                          <div className="chatCard__initials">
                            <img src={chat?.domainLogo ?? ImageIcon} alt="" />
                          </div>
                          <div className="chatCard__info">
                            <div className="chatCard__info__col">
                              <h4 className="name">
                                {capitalize(chat?.domain ?? "")}
                              </h4>
                              <p>{chat?.lastMessage}</p>
                            </div>
                            <div className="chatCard__info__col chatCard__info__col--info">
                              <p>{chat?.lastMessageTime}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flexbox">
                          <p className="para">
                            <AssistantIcon />
                            {chat?.assistantName}
                          </p>

                          <button className="btn btn__primary btn__outlined">
                            {chat?.widgetId}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <ul className="listCol__list listing__list">
                  <div className="no-data">No Chats Found!</div>
                </ul>
              )
            ) : Array.isArray(groupChatListData?.data) &&
              groupChatListData?.data?.length ? (
              <ul className="listCol__list listCol__list--groupChat">
                {groupChatListData?.data?.map((chat) => {
                  const isSelected = isSelectedChat(chat);
                  return (
                    <li
                      className={`listCol__list__item ${
                        isSelected ? "listCol__list__item--selected" : ""
                      }`}
                      onClick={() => {
                        navigate(
                          `/chat/group/${chat?.contactGroupId}/${chat?.assistantId}`,
                          {
                            state: {
                              assistantName: chat?.assistantName,
                            },
                          }
                        );
                      }}
                    >
                      <div className="chatCard">
                        <div className="chatCard__initials">
                          {chat?.groupLogo ? (
                            <img src={chat?.groupLogo} alt="" />
                          ) : (
                            capitalize(String(chat?.contactGroupName ?? ""))[0]
                          )}
                        </div>
                        <div className="chatCard__info">
                          <div className="chatCard__info__col">
                            <h4 className="name">
                              {capitalize(chat?.contactGroupName ?? "")}
                            </h4>
                            <p className="textPrimary">
                              <AssistantIcon />
                              {chat?.assistantName}
                            </p>
                          </div>
                          <div className="chatCard__info__col">
                            <button className="btn border_0 p_0">
                              <OrangeRightIcon />
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <ul className="listCol__list listCol__list--groupChat">
                <div className="no-data">No Chats Found!</div>
              </ul>
            )
          ) : (
            <ul className="listCol__list listing__list"></ul>
          )}
        </div>
      </div>
      <Pagination
        isChatPagination
        apiMethod={(page)=>getChatListData(page)}
        itemsPerPage={localItemsPerPage}
        setItemsPerPage={setLocalItemsPerPage}
      />
    </div>
  );
};
